import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { compare } from 'compare-versions';
import { LoadingPopupComponent } from 'src/app/components/ui/loading-popup/loading-popup.component';
import { DeviceService } from 'src/app/services/devices/device.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/modals/confirmation-modal/confirmation-modal.component';

const FW_POLL_INTERVAL_MS = 2000;
const FW_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const FW_DONE_STATES = ['Completed', 'not_initialized'];
const FW_FAILED_STATES = ['Cancelled', 'Failed'];

@Component({
    selector: 'app-fwupdate',
    templateUrl: './fwupdate.component.html',
    standalone: false
})
export class FwupdateComponent implements OnInit, OnDestroy {

  latestVersion: any;

  availableNewVersion = false;

  device: any;
  serial: string = '';

  error: string = '';
  successMessage: boolean = false;

  cortexStep: 'idle' | 'sent' | 'started' | 'downloading' | 'programming' | 'rebooting' | 'done' | 'failed' | 'timeout' = 'idle';
  cortexMessage = '';
  cortexTimestamp = '';
  cortexError = '';

  // Pasos que el firmware aún no emite: solo se muestran si llega su evento
  private readonly optionalSteps = ['downloading', 'programming', 'rebooting'];
  private cortexSeenSteps: string[] = [];

  originalData: any;
  changesMade=false;
  modalLoad: NgbModalRef;
  pollInterval: any;

  private cortexPoll: ReturnType<typeof setInterval>;
  private cortexPollStarted = 0;

  @Input()
  allSettings = false;

  @Input()
  modules: string[] = [];

  constructor(public _device: DeviceService, private _aRoute: ActivatedRoute, private _modal: ModalService, private _translate: TranslateService) { }

  ngOnInit(): void {

    this._aRoute.parent?.parent?.params
      .subscribe((params: Params) => this.serial = params['serial']);

    this._device.getDeviceInfo(this.serial)
      .subscribe(data => {
        this.device = data;
        this._device.getLatestVersionDevice(this.serial)
          .subscribe(data => {
            this.latestVersion = data;
            this.availableNewVersion = this.hasNewVersion(data);
            if (this.hasCortexModule && this.latestVersion?.cortex) {
              this.loadCortexStat();
            }
          }, () => {
            this.availableNewVersion = false;
          })
      })
  }

  ngOnDestroy(): void {
    this.stopCortexPoll();
    clearInterval(this.pollInterval);
  }

  get hasCortexModule(): boolean {
    return this.modules.includes('cortex_fw_update');
  }

  get cortex() {
    return this.hasCortexModule ? this.latestVersion?.cortex : null;
  }

  get cortexUpdating(): boolean {
    return ['sent', 'started', 'downloading', 'programming', 'rebooting'].includes(this.cortexStep);
  }

  get visibleCortexSteps(): { label: string, reached: boolean, active: boolean }[] {
    const base = ['sent', 'started'];
    const order = [...base, ...this.optionalSteps.filter(s => this.cortexSeenSteps.includes(s))];
    const labels: Record<string, string> = {
      sent: 'cortexStepSent',
      started: 'cortexStepStarted',
      downloading: 'cortexStepDownloading',
      programming: 'cortexStepProgramming',
      rebooting: 'cortexStepRebooting'
    };
    // "sent" no llega como evento: al reabrir la pantalla durante la actualización se da por alcanzado
    const current = Math.max(order.indexOf(this.cortexStep), 0);
    return order.map((step, i) => ({
      label: labels[step],
      reached: current > i,
      active: current === i
    }));
  }

  update() {
    if (!this.hasNewVersion(this.latestVersion)) {
      return;
    }
    const modalConfirm = this._modal.open(ConfirmationModalComponent);
    modalConfirm.componentInstance.header = this._translate.instant('do_update');
    modalConfirm.componentInstance.body = this._translate.instant('fwUpdateConfirmBody', { version: this.latestVersion.name });
    modalConfirm.componentInstance.emitService.subscribe((valueReceived) => {
      if (valueReceived) {
        modalConfirm.close();
        this.sendFwUpdate();
      }
    });
  }

  private sendFwUpdate() {
    this.modalLoad = this._modal.open(LoadingPopupComponent);
    this.modalLoad.componentInstance.message = this._translate.instant('fw_updating');
    this._device.updateFw(this.serial, this.latestVersion.url)
      .subscribe(() => {
        // El PUT solo confirma que el dispositivo aceptó el comando: la descarga/flasheo
        // reales se siguen a través del módulo fw_update (mismo endpoint que usa la app
        // móvil para su barra de progreso).
        this.pollFirmwareProgress();
      }, err => {
        console.error(err);
        this.error = err?.error?.message || '';
        setTimeout(() => {
          this.error = '';
        }, 10000);
        this.modalLoad.close();
      });
  }

  private hasNewVersion(v: any): boolean {
    const name = typeof v?.name === 'string' ? v.name.trim() : '';
    const actual = typeof this.device?.firmware_version_name === 'string'
      ? this.device.firmware_version_name.trim()
      : '';
    if (!v || v.error || !name || !v.url) {
      return false;
    }
    try {
      return compare(name, actual, '>');
    } catch {
      return name !== actual;
    }
  }

  updateCortex() {
    if (!this.cortex?.available?.url) {
      return;
    }
    this.cortexError = '';
    this.cortexStep = 'idle';
    this.cortexMessage = '';
    this.cortexTimestamp = '';
    this.cortexSeenSteps = [];
    this.modalLoad = this._modal.open(LoadingPopupComponent);
    this._device.updateCortexFw(this.serial, this.cortex.available.url)
      .subscribe(() => {
        this.modalLoad.close();
        this.cortex.updateInProgress = true;
        this.cortexStep = 'sent';
        this.startCortexPoll();
      }, err => {
        console.error(err);
        this.cortexError = err?.error?.message || '';
        this.modalLoad.close();
      });
  }

  private loadCortexStat() {
    this._device.getCortexFwUpdateStat(this.serial).subscribe(data => {
      const stat = data?.stat;
      if (stat?.event) {
        this.applyCortexStat(stat);
      }
      if (this.cortex?.updateInProgress && this.cortexUpdating) {
        this.startCortexPoll();
      }
    }, () => {
      if (this.cortex?.updateInProgress) {
        this.startCortexPoll();
      }
    });
  }

  private startCortexPoll() {
    this.stopCortexPoll();
    this.cortexPollStarted = Date.now();
    this.cortexPoll = setInterval(() => this.readCortexStat(), 5000);
  }

  private readCortexStat() {
    if (Date.now() - this.cortexPollStarted > 15 * 60 * 1000) {
      this.cortexStep = 'timeout';
      if (this.cortex) {
        this.cortex.updateInProgress = false;
      }
      this.stopCortexPoll();
      return;
    }
    this._device.getCortexFwUpdateStat(this.serial).subscribe(data => {
      const stat = data?.stat;
      if (!stat?.event) {
        return;
      }
      this.applyCortexStat(stat);
      if (!this.cortexUpdating) {
        if (this.cortex) {
          this.cortex.updateInProgress = false;
        }
        this.stopCortexPoll();
        if (this.cortexStep === 'done') {
          this.refreshLatestVersion();
        }
      }
    }, () => {});
  }

  private applyCortexStat(stat: { event: string, message?: string, timestamp?: string }) {
    const step = this.eventToStep(stat.event);
    if (this.optionalSteps.includes(step) && !this.cortexSeenSteps.includes(step)) {
      this.cortexSeenSteps.push(step);
    }
    this.cortexStep = step;
    this.cortexMessage = stat.message || '';
    this.cortexTimestamp = stat.timestamp || '';
  }

  private eventToStep(event: string): typeof this.cortexStep {
    const map: Record<string, typeof this.cortexStep> = {
      started: 'started',
      descargando: 'downloading',
      downloading: 'downloading',
      programando: 'programming',
      programming: 'programming',
      reiniciando: 'rebooting',
      rebooting: 'rebooting',
      update_done: 'done',
      completed: 'done',
      ok: 'done',
      failed: 'failed',
      error: 'failed'
    };
    return map[event] || 'started';
  }

  private refreshLatestVersion() {
    this._device.getLatestVersionDevice(this.serial).subscribe(data => {
      this.latestVersion = data;
      this.availableNewVersion = this.hasNewVersion(data);
    }, () => {});
  }

  private stopCortexPoll() {
    if (this.cortexPoll) {
      clearInterval(this.cortexPoll);
      this.cortexPoll = null;
    }
  }

  private pollFirmwareProgress() {
    const startedAt = Date.now();
    let sawDownloading = false;
    let finished = false;

    const finish = (success: boolean) => {
      if (finished) return;
      finished = true;
      this.finishUpdate(success);
    };

    this.pollInterval = setInterval(() => {
      this._device.getModuleInfo(this.serial, 'fw_update', 'stat').subscribe(stat => {
        if (stat?.state === 'Downloading') {
          sawDownloading = true;
        }
        this.modalLoad.componentInstance.progress = stat?.progress?.percentage ?? null;

        // Tras 'Completed' el dispositivo reinicia y esta misma llamada puede devolver
        // 404 (ver catch) o 'not_initialized': ambos cuentan como fin si ya vimos Downloading.
        if (sawDownloading && FW_DONE_STATES.includes(stat?.state)) {
          finish(true);
        } else if (FW_FAILED_STATES.includes(stat?.state)) {
          finish(false);
        } else if (Date.now() - startedAt > FW_POLL_TIMEOUT_MS) {
          finish(false);
        }
      }, () => {
        // El dispositivo reinicia durante el flasheo y puede no responder unos segundos.
        if (sawDownloading && Date.now() - startedAt > FW_POLL_TIMEOUT_MS) {
          finish(false);
        }
      });
    }, FW_POLL_INTERVAL_MS);
  }

  private finishUpdate(success: boolean) {
    clearInterval(this.pollInterval);
    this.modalLoad.close();
    if (success) {
      this.availableNewVersion = false;
      this.successMessage = true;
      setTimeout(() => {
        this.successMessage = false;
      }, 10000);
    } else {
      this.error = this._translate.instant('errorUpdateFW');
      setTimeout(() => {
        this.error = '';
      }, 10000);
    }
  }

}
